/**
 * RunEvaluationDialog Component
 * Modal for running evaluations and displaying results
 * Modern, clean design with better UX
 */

import React, { useState, useEffect } from 'react';
import { X, Play, AlertCircle, CheckCircle, XCircle, Rocket, Bot, Target, Gauge, Sparkles, FolderOpen, User, Languages } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { EvalScenario, EvalRunResult, MetricResult, EvalTestSuite, TestSuiteRunResult } from '@/types/evalScenarios';
import { EvalUserPicker } from './EvalUserPicker';

interface RunEvaluationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRunSingle: (scenarioId: string, userId?: string | null, responseLanguage?: string | null) => Promise<EvalRunResult>;
  onRunBatch: (userId?: string | null, responseLanguage?: string | null) => Promise<EvalRunResult>;
  onRunSuite?: (suiteId: string, userId?: string | null, responseLanguage?: string | null) => Promise<TestSuiteRunResult>;
  scenario?: EvalScenario | null;
  scenarios?: EvalScenario[];
  suites?: EvalTestSuite[];
}

export const RunEvaluationDialog: React.FC<RunEvaluationDialogProps> = ({
  isOpen,
  onClose,
  onRunSingle,
  onRunBatch,
  onRunSuite,
  scenario,
  scenarios = [],
  suites = [],
}) => {
  const { messages, language } = useI18n();
  const t = messages.evalScenarios;
  const [isRunning, setIsRunning] = useState(false);
  const [runResult, setRunResult] = useState<EvalRunResult | null>(null);
  const [selectedSuiteId, setSelectedSuiteId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [responseLanguage, setResponseLanguage] = useState<'auto' | 'zh' | 'en'>('auto');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset selections when dialog opens, pre-populate from scenario default
      setSelectedSuiteId('');
      setSelectedUserId(scenario?.default_user_id || null);
      setResponseLanguage('auto');
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, scenario?.default_user_id]);

  if (!isOpen) return null;

  const isSingleRun = !!scenario;
  const activeScenarios = scenarios.filter((s) => s.is_active);
  const enabledCriteria = scenario?.criteria.filter((c) => c.enabled !== false) || [];

  const formatScore = (score: number | undefined | null): string => {
    if (score === null || score === undefined) return '-';
    return (score * 100).toFixed(0) + '%';
  };

  const handleRun = async () => {
    setIsRunning(true);
    setRunResult(null);

    // Resolve response language: 'auto' means use current UI language
    const effectiveLang = responseLanguage === 'auto' ? language : responseLanguage;

    try {
      let result: EvalRunResult;
      if (isSingleRun && scenario) {
        result = await onRunSingle(scenario.id, selectedUserId, effectiveLang);
      } else if (selectedSuiteId && onRunSuite) {
        // Run selected suite
        result = await onRunSuite(selectedSuiteId, selectedUserId, effectiveLang);
      } else {
        result = await onRunBatch(selectedUserId, effectiveLang);
      }
      setRunResult(result);
    } catch (error) {
      setRunResult({
        overall_score: 0,
        passed: false,
        metrics: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleClose = () => {
    setRunResult(null);
    setIsRunning(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Play className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isSingleRun ? t.runSingle : t.runAll}
              </h2>
              {isSingleRun && scenario && (
                <p className="text-sm text-white/80 mt-0.5">{scenario.name}</p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Running State */}
          {isRunning && (
            <div className="text-center py-12">
              <div className="relative inline-block">
                <div className="w-20 h-20 border-4 border-indigo-100 rounded-full" />
                <div className="absolute inset-0 w-20 h-20 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-indigo-600" />
              </div>
              <p className="mt-6 text-lg font-medium text-gray-900">{t.runningEvaluation}</p>
              <p className="mt-2 text-sm text-gray-500">
                {t.runningHint}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {t.runningHintResult}
              </p>
            </div>
          )}

          {/* Pre-run Info */}
          {!isRunning && !runResult && (
            <>
              {isSingleRun && scenario && (
                <div className="space-y-4">
                  {/* Scenario Info Card */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 space-y-4">
                    {/* Agent & Model Row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Bot className="w-4 h-4 text-indigo-500" />
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {t.agentType}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{scenario.agent_type}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Gauge className="w-4 h-4 text-purple-500" />
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {t.judgerModel}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{scenario.judger_model_id}</p>
                      </div>
                    </div>

                    {/* Criteria */}
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t.evaluationCriteria}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({enabledCriteria.length} {t.active})
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {enabledCriteria.map((c) => (
                          <div
                            key={c.name}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 rounded-lg"
                          >
                            <span className="text-sm font-medium text-indigo-700">{c.name}</span>
                            <span className="text-xs text-indigo-500 bg-indigo-100 px-1.5 py-0.5 rounded">
                              {formatScore(c.threshold)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Min Score */}
                    <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
                      <span className="text-sm text-gray-600">{t.minOverallScore}</span>
                      <span className="text-lg font-bold text-indigo-600">
                        {formatScore(scenario.min_overall_score)}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  {scenario.description && (
                    <p className="text-sm text-gray-500 text-center px-4">
                      {scenario.description}
                    </p>
                  )}
                </div>
              )}

              {!isSingleRun && (
                <div className="space-y-6">
                  {/* Suite Selector */}
                  {suites.length > 0 && (
                    <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <FolderOpen className="w-5 h-5 text-violet-600" />
                        <h3 className="font-medium text-gray-900">
                          {t.testSuites?.selectSuite}
                        </h3>
                      </div>
                      <select
                        value={selectedSuiteId}
                        onChange={(e) => setSelectedSuiteId(e.target.value)}
                        className="w-full px-4 py-2 border border-violet-200 rounded-lg bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      >
                        <option value="">
                          {t.testSuites?.runAllActive}
                        </option>
                        {suites.filter(s => s.is_active).map((suite) => (
                          <option key={suite.id} value={suite.id}>
                            {suite.name} ({suite.scenario_count} {t.testSuites?.scenarios})
                          </option>
                        ))}
                      </select>
                      {selectedSuiteId && (
                        <p className="mt-2 text-sm text-violet-600">
                          {suites.find(s => s.id === selectedSuiteId)?.description || ''}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Batch Info */}
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Rocket className="w-8 h-8 text-indigo-600" />
                    </div>
                    {selectedSuiteId ? (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {t.testSuites?.runSuite}
                        </h3>
                        <p className="text-gray-600">
                          {t.testSuites?.runSuiteInfo}{' '}
                          <span className="font-bold text-violet-600">
                            {suites.find(s => s.id === selectedSuiteId)?.name}
                          </span>
                          {' '}({suites.find(s => s.id === selectedSuiteId)?.scenario_count} {t.testSuites?.scenarios})
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {t.runAll}
                        </h3>
                        <p className="text-gray-600">
                          {t.batchInfoPrefix}{' '}
                          <span className="font-bold text-indigo-600">{activeScenarios.length}</span>{' '}
                          {t.batchInfoSuffix}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* User Picker */}
              <div className="mt-4 bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl p-5">
                <EvalUserPicker
                  selectedUserId={selectedUserId}
                  onSelect={setSelectedUserId}
                />
              </div>

              {/* Response Language Selector */}
              <div className="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Languages className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t.responseLanguage.title}
                  </span>
                </div>
                <select
                  value={responseLanguage}
                  onChange={(e) => setResponseLanguage(e.target.value as 'auto' | 'zh' | 'en')}
                  className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="auto">{t.responseLanguage.auto}</option>
                  <option value="zh">{t.responseLanguage.zh}</option>
                  <option value="en">{t.responseLanguage.en}</option>
                </select>
                <p className="mt-1.5 text-xs text-gray-500">{t.responseLanguage.hint}</p>
              </div>

              {/* Run Button */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleRun}
                  className="group relative px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 transition-all duration-200 hover:-translate-y-0.5"
                >
                  <span className="flex items-center gap-2">
                    <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    {t.runEvaluationButton}
                  </span>
                </button>
              </div>
            </>
          )}

          {/* Results */}
          {!isRunning && runResult && (
            <div className="space-y-4">
              {/* Error */}
              {runResult.error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700">{runResult.error}</p>
                </div>
              )}

              {/* Background Execution Started */}
              {!runResult.error && runResult.status === 'running' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Rocket className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t.evaluationStarted}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {runResult.message || t.evaluationStartedHint}
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 inline-block">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">{t.userPicker.runId}:</span> {runResult.run_id}
                    </p>
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">{t.userPicker.scenarios}:</span> {runResult.total_scenarios}
                    </p>
                    {selectedUserId && (
                      <p className="text-sm text-blue-800 flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        <span className="font-medium">{t.userPicker.user}:</span> {selectedUserId.length > 20 ? selectedUserId.slice(0, 8) + '...' + selectedUserId.slice(-8) : selectedUserId}
                      </p>
                    )}
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    {t.checkRunsPanel}
                  </p>
                </div>
              )}

              {/* Completed Result */}
              {!runResult.error && runResult.status !== 'running' && runResult.passed !== null && (
                <>
                  {/* Overall Score */}
                  <div className="text-center py-6">
                    <div
                      className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${
                        runResult.passed === true
                          ? 'bg-gradient-to-br from-emerald-400 to-green-500'
                          : 'bg-gradient-to-br from-red-400 to-rose-500'
                      }`}
                    >
                      <span className="text-3xl font-bold text-white">
                        {formatScore(runResult.overall_score)}
                      </span>
                    </div>
                    <div
                      className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                        runResult.passed === true
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {runResult.passed === true ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                      <span className="font-semibold">
                        {runResult.passed === true ? t.passed.toUpperCase() : t.failed.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Metrics */}
                  {runResult.metrics && runResult.metrics.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700 mb-3">{t.metricResults}</h4>
                      {runResult.metrics.map((metric: MetricResult) => {
                        const isLowerBetter = ['hallucination', 'bias', 'toxicity'].includes(metric.name);
                        const metricKey = metric.name as keyof typeof t.metrics;
                        const metricLabel = metric.display_name || t.metrics[metricKey] || metric.name;

                        return (
                          <div
                            key={metric.name}
                            className={`p-4 rounded-xl border-2 ${
                              metric.passed
                                ? 'bg-emerald-50 border-emerald-200'
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {metric.passed ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                                <div>
                                  <span className="font-medium text-gray-900">{metricLabel}</span>
                                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                    isLowerBetter
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {isLowerBetter ? t.lowerIsBetter : t.higherIsBetter}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <span
                                  className={`text-xl font-bold ${
                                    metric.passed ? 'text-emerald-600' : 'text-red-600'
                                  }`}
                                >
                                  {formatScore(metric.score)}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {t.threshold}: {formatScore(metric.threshold)}
                                </p>
                              </div>
                            </div>
                            {metric.reason &&
                              (metric.score === 0 ||
                                metric.reason.toLowerCase().includes('error')) && (
                                <div className="mt-3 text-xs text-red-600 bg-red-100 p-2 rounded-lg flex items-start gap-1">
                                  <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>{metric.reason}</span>
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Response Preview */}
                  {runResult.response && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">{t.response}</h4>
                      <div className="bg-gray-50 p-4 rounded-xl text-sm max-h-40 overflow-y-auto border border-gray-200">
                        <p className="whitespace-pre-wrap">
                          {runResult.response.substring(0, 500)}
                          {runResult.response.length > 500 && '...'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Batch Results */}
                  {runResult.total_scenarios !== undefined && (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-5 rounded-xl">
                      <h4 className="font-medium text-gray-700 mb-4">{t.batchSummary}</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <div className="text-3xl font-bold text-emerald-600">
                            {runResult.passed_scenarios}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{t.passed}</div>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <div className="text-3xl font-bold text-red-600">
                            {runResult.failed_scenarios}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{t.failed}</div>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <div className="text-3xl font-bold text-gray-700">
                            {runResult.total_scenarios}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{t.total}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <Button variant="secondary" onClick={handleClose}>
            {t.close}
          </Button>
        </div>
      </div>
    </div>
  );
};
